import type {
  KizenNodeType,
  HistoryDetailedLog,
  KizenTriggerType,
} from 'pages/AutomationEngine/types';
import { TFunction } from 'i18next';
import { AUTOMATION_VARIABLE_DATA_TYPES } from 'pages/AutomationEngine/constants';
import { type HistoryWithStep } from './useHistoryNavigation';
import { getDate } from 'components/Inputs/DateTimeInput/helpers';
import { getSubtypeLabel } from '../helpers';
import type { AutomationStep } from 'ts-components/Plugins/types';

const stepTitle = (type: KizenNodeType, t: TFunction) => {
  switch (type) {
    case 'condition':
      return t('Condition');
    case 'goal':
      return t('Goal');
    case 'delay':
      return t('Delay');
    case 'initialize_variable':
      return t('Variable');
    default:
      return t('Action');
  }
};

const getDateConfig = (timeZone?: string): Intl.DateTimeFormatOptions => ({
  month: '2-digit',
  day: '2-digit',
  year: 'numeric',
  timeZone,
});

const getTimeConfig = (timeZone?: string): Intl.DateTimeFormatOptions => ({
  minute: '2-digit',
  hour: '2-digit',
  timeZone,
});

export const historyTitle = (
  history: HistoryWithStep,
  automationSteps: AutomationStep<{ name: string }>[],
  t: TFunction
) => {
  let title = history.step
    ? getSubtypeLabel(
        history.step.type === 'initialize_variable'
          ? {
              type: history.step.type,
              subtype:
                history.step.details?.dataType ??
                history.detailedLog?.variable_type,
            }
          : history.step.type === 'condition'
            ? {
                type: history.step.type,
                subtype: history.step.details?.type as any,
              }
            : history.step.type === 'goal'
              ? {
                  type: (history.step.details?.type || '').replace(
                    /_goal$/,
                    ''
                  ) as KizenTriggerType,
                }
              : history.step.type === 'plugin_code_step'
                ? {
                    type: history.step.type,
                    subtype:
                      automationSteps.find(
                        ({ plugin_api_name }) =>
                          plugin_api_name ===
                          history.step?.details?.pluginAppApiName
                      )?.actions?.[history.step?.details?.actionStepApiName]
                        ?.name ?? '',
                  }
                : { type: history.step.type },
        t
      )
    : null;

  if (!title) {
    title =
      history.stepId && history.step
        ? stepTitle(history.step.type, t)
        : history.triggerId
          ? t('Trigger')
          : t('Variable');
  }

  return [title, history.step?.deleted && `${t('[Deleted]')}`]
    .filter(Boolean)
    .join(' ');
};

export const getHistoryVariableValue = (
  detailedLog: HistoryDetailedLog,
  businessTimezone: string,
  t: TFunction
) => {
  const {
    value,
    value_human_readable,
    variable_source_subtype,
    variable_type,
    is_array,
  } = detailedLog;

  if (is_array) {
    return Array.isArray(value) && value?.length
      ? t('[{{amount}} {{items}}]', {
          amount: value.length,
          items: value.length > 1 ? t('items') : t('item'),
        })
      : '[]';
  }

  // these types will always have valueHumanReadable supplied
  // so we can by pass the sub type checking

  if (
    variable_type === AUTOMATION_VARIABLE_DATA_TYPES.employee ||
    variable_type === AUTOMATION_VARIABLE_DATA_TYPES.entity
  ) {
    return `"${value_human_readable || value || ''}"`;
  }

  switch (variable_type) {
    case AUTOMATION_VARIABLE_DATA_TYPES.date: {
      if (!value) {
        return '""';
      }

      return `"${getDate(value)}"`;
    }
    case AUTOMATION_VARIABLE_DATA_TYPES.datetime: {
      if (!value) {
        return '""';
      }

      const local = `"${new Date(value).toLocaleString('en-US', getDateConfig())} at ${new Date(value).toLocaleTimeString('en-US', getTimeConfig())}"`;
      const business = `${new Date(value).toLocaleString('en-US', getDateConfig(businessTimezone))} at ${new Date(value).toLocaleTimeString('en-US', getTimeConfig(businessTimezone))} (${businessTimezone})`;

      return { text: local, tooltip: business };
    }
    default:
      break;
  }

  switch (variable_source_subtype) {
    case AUTOMATION_VARIABLE_DATA_TYPES.employee:
    case AUTOMATION_VARIABLE_DATA_TYPES.entity:
      return `"${value_human_readable}"`;
    default:
      return `"${value || ''}"`;
  }
};
